import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

// Mesma ordem visual do NAV — usada para redirecionar o user para a primeira
// aba que ele tem permissão de ver.
const FALLBACK_ORDER = [
  { path: "/", perms: ["painel:view"] },
  { path: "/entrada", perms: ["entrada:individual", "entrada:lote", "entrada:ver_docs"] },
  { path: "/romaneio", perms: ["romaneio:view"] },
  { path: "/devolucao", perms: ["devolucao:executar"] },
  { path: "/rolos", perms: ["estoque:view"] },
  { path: "/cadastro", perms: ["cadastro:fornecedores_view", "cadastro:combinacoes_view"] },
];

function firstAllowedPath(user, hasPerm) {
  if (user?.is_master) return "/";
  for (const item of FALLBACK_ORDER) {
    if (item.perms.some((p) => hasPerm(p))) return item.path;
  }
  return null;
}

/**
 * Envolve uma rota. Se o user não tem permissão, redireciona para a primeira
 * aba permitida ou para /sem-acesso se ele não tiver nenhuma.
 * Props:
 *   perm: string (chave única, ex "painel:view") — permite se hasPerm(perm)
 *   perms: string[] (qualquer uma libera)
 *   masterOnly: boolean (só admin master libera)
 */
export function RequireRoute({ perm, perms, masterOnly, children }) {
  const { user, hasPerm } = useAuth();
  const location = useLocation();

  if (!user) return null; // ainda hidratando

  // Master sempre libera (exceto quando explicitamente veto do backend — o próprio backend responde 403 se master perder alguma chave admin_only)
  if (user.is_master) return children;

  const allowed =
    (masterOnly && user.is_master) ||
    (perm && hasPerm(perm)) ||
    (perms && perms.some((p) => hasPerm(p)));

  if (allowed) return children;

  const fallback = firstAllowedPath(user, hasPerm);
  if (!fallback) {
    // Não tem NENHUMA aba permitida
    if (location.pathname === "/sem-acesso") return children; // evita loop
    return <Navigate to="/sem-acesso" replace />;
  }
  return <Navigate to={fallback} replace />;
}
